import { createSelector } from '@reduxjs/toolkit';
import { StatsEventBase } from '@yandex-360/telemost-sdk/models/stats';
import { ParticipantSlot, SlotType } from '@yandex-video-platform/goloom-sdk';
import _get from 'lodash/get';

import { RootState } from '../../../init-store';
import { CLIENT_ID, SELF } from '../../shared/constants';
import {
    getAppVersionSelector,
    getConfig,
    getIsDisableLocalRecording,
    getIsOneOnOneEnabled,
    getTelemostAgent,
    isMessengerIntegration,
    isProductionEnvironmentSelector,
    selectIsB2BHost,
    selectIsMeetingRoomIntegration,
    selectIsSferusIntegration,
    selectIsTeleMessenger,
    selectSyncStateWithPeerId,
} from '../../shared/features/config/selectors';
import { DeviceKind } from '../../shared/features/device-manager/slice';
import { selectToggleNewGrid } from '../../shared/features/flags/selectors';
import { goloomSdk } from '../../shared/features/goloom-sdk';
import { getShowMyVideo } from '../../shared/features/settings';
import { isMobileUa } from '../../shared/features/uatraits/selectors';
import { isB2bUserSelector } from '../../shared/features/user/selectors';
import { getDefaultAvatarPlaceholder } from '../../shared/helpers/avatar-placeholder';
import { isInsideIframe } from '../../shared/helpers/iframe';
import parseCallUri from '../../shared/helpers/parse-call-uri';
import { PersonalPermission } from '../../shared/helpers/permissions';
import { sortParticipants } from '../../shared/helpers/sort-participants';
import { AccessLevel, BroadcastStatus } from '../../types';
import { ConferenceStatus, GRID_VIEW, NetworkQualityType, NetworkState, PeersTabValue } from './constants';
import { selectIsCloudRecordingRunning } from './features/cloud-recording/selectors';
import { selectSlotsMaxOffset } from './features/conference/selectors';
import { selectIsFullscreen } from './features/fullscreen';
import {
    isRecordingAllowedSelector,
    selectHasAdmitAllPermission,
    selectHasAdmitPermission,
    selectHasConferencePermissionsEditMatrixPermission,
} from './features/permissions';
import { isRecordAllowed } from './helpers/is-record-allowed';
import {
    ConferenceConfig,
    OutgoingCallStatuses,
    Participant,
    ParticipantID,
    ParticipantsById,
    ParticipantState,
    SidebarContent,
    WaitingRoomPeersSort,
} from './types';

export const selectConference = (state: RootState) => {
    return state.call.conference;
};

export const getConferenceStateVersion = (state: RootState) => {
    return state.call.conference.stateVersion;
};

export const getConferenceId = ({
    call: {
        conference: { config },
    },
}: RootState): string => (config ? parseCallUri(config.uri).roomId : '');

export const getRoomId = (state: RootState): ConferenceConfig['room_id'] => {
    const config = getConfig(state);
    const conferenceConfig = getConferenceConfig(state);

    return conferenceConfig?.room_id || config.roomId || '';
};

export const selectParticipants = (state: RootState) => {
    return state.call.conference.participants;
};
export const getMyUserId = (state: RootState) => {
    return state.call.conference.myUserId;
};
export const selectVideoGrid = (state: RootState) => {
    return state.call.videoGrid;
};
export const getGridItemRatio = (state: RootState) => {
    return state.call.videoGrid.gridItemRatio;
};

export const getUserPreferredGridView = (state: RootState): GRID_VIEW => {
    return state.call.videoGrid.userPreferredGridView;
};

export const getDesktopSharingStatus = (state: RootState) => {
    return state.call.screenSharing.desktopSharingStatus;
};

export const getConferenceConfig = (state: RootState) => {
    return state.call.conference.config;
};

export const getConferenceSettings = (state: RootState) => {
    return state.call.conference.settings;
};

export const getJoinedAsGuest = (state: RootState) => {
    return state.call.conference.joinedAsGuest;
};

export const getMyOldUserIds = (state: RootState) => {
    return state.call.conference.myOldUserIds;
};

export const getParticipant = (state: RootState, id: ParticipantID): Participant => {
    const {
        call: {
            conference: { participantsByIds },
        },
    } = state;
    const participant = participantsByIds[id] || { id };

    return {
        id,
        uid: participant.uid,
        displayName: participant.displayName,
        avatarUrl: participant.avatarUrl,
        avatarPlaceholder: participant.avatarPlaceholder || getDefaultAvatarPlaceholder(participant.displayName),
        shouldRenderAvatar: Boolean(participant.avatarUrl && !participant.isDefaultAvatar),
        isModerator: participant.isModerator ?? false,
        isOwner: participant.isOwner ?? false,

        audioTrackState: participant.audio,
        videoTrackState: participant.video,
        desktopTrackState: participant.desktop,

        isLocalRecording: participant.isLocalRecording ?? false,
        version: participant.version,
        reaction: participant.reaction,
        emotion: participant.emotion,
        peerType: participant.peerType,
        refParticipantId: participant.refParticipantId || null,
    };
};

export const getParticipants = createSelector(
    [
        (state: RootState) => state.call.conference.participants,
        (state: RootState) => state.call.conference.participantsByIds,
        (state: RootState) => state,
    ],
    (participants, participantsByIds, state) => {
        const result = participants.reduce<Participant[]>((acc, id) => {
            if (participantsByIds[id]) {
                acc.push(getParticipant(state, id));
            }
            return acc;
        }, []);

        const hasSingleParticipant = result.length === 1;
        if (hasSingleParticipant) {
            return [{ ...result[0], isDominant: false }];
        }

        return result;
    }
);

export const getParticipantsCount = (state: RootState) => state.call.conference.participants.length;

export const getIsOneOnOne = (state: RootState) => {
    return getIsOneOnOneEnabled(state);
};

export const getIsWaitingForCallee = (state: RootState): boolean => {
    return getIsOneOnOne(state) && getParticipantsCount(state) === 1;
};

export const getCallee = (state: RootState): Maybe<Participant> => {
    const { callee } = state.call.messenger.outgoingCall;

    if (!callee) {
        return;
    }

    return {
        ...callee,
        isOwner: false,
        isLocalRecording: false,
        isModerator: false,
        shouldRenderAvatar: Boolean(callee.avatarUrl),
        avatarPlaceholder: callee.avatarPlaceholder || getDefaultAvatarPlaceholder(callee.displayName),
    };
};

export const getCallStatus = (state: RootState): OutgoingCallStatuses => {
    return state.call.messenger.outgoingCall.status;
};

export const getActiveGridView = (state: RootState): GRID_VIEW => {
    return getIsWaitingForCallee(state) && getCallee(state) ? GRID_VIEW.PRESENTER : state.call.videoGrid.activeGridView;
};

export const getParticipantByUid = (state: RootState, uid: string) => {
    return getParticipants(state).find((participant) => uid === participant.uid);
};

export const getOtherRecordingParticipants = (state: RootState) => {
    const participants = getParticipants(state);
    return participants.filter(({ id, isLocalRecording }) => isLocalRecording && id !== 'self');
};
export const selectDevice = (state: RootState) => {
    return state.call.device;
};

/**
 * @deprecated в пользу selectMaxDisplayParticipants
 */
export const getMaxDisplayParticipants = (state: RootState) => {
    return state.call.videoGrid.maxDisplayParticipants;
};

export const selectMaxDisplayParticipants = (state: RootState) => {
    let maxDisplayParticipants = getMaxDisplayParticipants(state);
    const isFullscreen = selectIsFullscreen(state);
    if (isFullscreen) {
        maxDisplayParticipants = 0;
    }
    return maxDisplayParticipants;
};

export const getCurrentPage = (state: RootState) => {
    return state.call.videoGrid.currentPage;
};

export const getParticipantsBySettings = (state: RootState): Participant[] => {
    const showMyVideo = getShowMyVideo(state);
    const participants = getParticipants(state);
    const syncStateWithPeerId = selectSyncStateWithPeerId(state);
    if (!showMyVideo && participants.length > 1) {
        return participants.filter((p) => p.id !== SELF && (!syncStateWithPeerId || p.id !== syncStateWithPeerId));
    }
    return participants;
};

/**
 * @param {RootState} state
 * @returns {Participant}
 */
export const getScreenSharingParticipant = createSelector([getParticipants], (participants) => {
    return participants.find((p) => p.desktopTrackState && !p.desktopTrackState.muted);
});

/**
 * Вернет доминантного участника
 *
 * @param {RootState} state
 * @returns {Participant}
 */
export const getDominantSpeaker = (state: RootState): Participant | null => {
    if (!goloomSdk.session) {
        return getParticipant(state, SELF);
    }

    const participantsSlots = goloomSdk?.session?.slots?.current.filter(
        ({ type }) => type === SlotType.PARTICIPANT
    ) as ParticipantSlot[];
    const participantId = participantsSlots[0].participant?.userId ?? SELF;
    if (participantId === SELF && getTelemostAgent(state) === 'translator') {
        return null;
    }
    if (participantId) {
        return getParticipant(state, participantId);
    }
    return null;
};

const getVisibleParticipantsGoloom = (state: RootState): Participant[] => {
    if (!goloomSdk.session) {
        return [];
    }

    const {
        call: {
            videoGrid: { activeGridView, currentPage },
            screenSharing: { desktopSharingStatus },
        },
    } = state;
    const showMyVideo = getShowMyVideo(state);

    const participants = [];

    if (currentPage === 0 && showMyVideo) {
        participants.push(getParticipant(state, SELF));
    }

    const isGridViewPresenter = activeGridView === GRID_VIEW.PRESENTER;
    const skipPresenterSlot = desktopSharingStatus === 'none' && isGridViewPresenter;

    goloomSdk.session.slots.current.forEach((slot, index) => {
        if (index === 0 && skipPresenterSlot) {
            return;
        }

        if (slot && slot.type === SlotType.PARTICIPANT && slot.participant && slot.mediaStreamKind === 'DEVICES') {
            participants.push(getParticipant(state, slot.participant.userId));
        }
    });

    return participants;
};

export const getVisibleParticipants = (state: RootState): Participant[] => {
    return getVisibleParticipantsGoloom(state);
};

export const getConferenceStatus = (state: RootState): ConferenceStatus => state.call.conference.conferenceStatus;

export const selectIsJoined = (state: RootState): boolean => getConferenceStatus(state) === ConferenceStatus.JOINED;

export const selectIsJoining = (state: RootState): boolean => getConferenceStatus(state) === ConferenceStatus.JOINING;

export const getDisplayedNetworkState = (state: RootState): NetworkState => state.call.offline.displayedNetworkState;
export const getNetworkState = (state: RootState): NetworkState => state.call.offline.networkState;
export const getDegradedStateStartTimestamp = (state: RootState): number | null =>
    state.call.offline.degradedStateStartTimestamp;

export const selectNetworkQualityType = (state: RootState): Maybe<NetworkQualityType> =>
    state.call.networkQuality.networkQualityType;

export type GetParticipantResult = ParticipantState | undefined;
export const getParticipantById = (state: RootState, id: string): GetParticipantResult =>
    state.call.conference.participantsByIds[id];

/**
 * Селектор для определения того, что в конфе участвует только её организатор
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const isOnlyOrganizerJoined = (state: RootState): boolean => {
    const { participants, joinedAsGuest } = state.call.conference;
    return participants.length === 1 && participants[0] === SELF && !joinedAsGuest;
};

/**
 * Вернёт true если не все участники влезают в сетку
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const hasHiddenParticipants = (state: RootState): boolean => {
    const {
        settings: {
            media: { showMyVideo },
        },
        call: {
            videoGrid: { maxDisplayParticipants },
            conference: { participants },
        },
    } = state;
    const visibleParticipants = participants.length + (showMyVideo ? 0 : -1);
    return visibleParticipants > maxDisplayParticipants;
};

/**
 * Returns `participantsByIds`.
 *
 * @param {RootState} state
 * @returns {{string, Participant}}
 */
export const getParticipantByIds = (state: RootState): ParticipantsById => state.call.conference.participantsByIds;

/**
 * Return additional information for EB logging.
 *
 * @param {RootState} state
 * @returns {{uid: string, roomUrl: string, roomUserId: string}}
 */
export const getAdditional = (state: RootState) => ({
    uid: _get(state, 'user.uid'),
    roomUrl: _get(state, 'conference.config.uri'),
    roomUserId: _get(state, 'conference.myUserId'),
});

/**
 * Show name of participants only on hover in presenter mode https://st.yandex-team.ru/CHEMODAN-78108
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const isShowNameOnHoverExp = ({ config: { experiments } }: RootState): boolean => {
    return experiments?.flags.telemost_show_name_on_hover_exp === true;
};

export const isLocalRecordingAllowed = (state: RootState) => {
    const isChromeBased = state.ua.BrowserBase === 'Chromium';

    const conferenceSettings = state.call.conference.settings;
    const isAllowedByBackend = conferenceSettings?.local_recording_allowed ?? false;
    const isDisableLocalRecording = getIsDisableLocalRecording(state);
    const isRecordingAllowedByPermissions = isRecordingAllowedSelector(state);
    const isAllowedByBrowser = isRecordAllowed();

    return (
        isChromeBased &&
        isAllowedByBackend &&
        !isDisableLocalRecording &&
        isRecordingAllowedByPermissions &&
        isAllowedByBrowser
    );
};

export const isLocalRecordingActive = (state: RootState) => state.call.record.isRecording;

export const isChatAllowedInConference = (state: RootState) => {
    const conferenceSettings = state.call.conference.settings;
    return !selectIsSferusIntegration(state) && (conferenceSettings?.chat_allowed ?? false);
};

export const getChatWidgetVisible = (state: RootState) => {
    return state.call.sidebar.sidebarContent === SidebarContent.Chat;
};

export const isSidebarVisible = (state: RootState) => {
    const { sidebarContent } = state.call.sidebar;

    return sidebarContent === SidebarContent.Chat || sidebarContent === SidebarContent.Participants;
};

export const getSidebarContent = (state: RootState) => {
    return state.call.sidebar.sidebarContent;
};

export const selectSidebarPeersTab = (state: RootState) => {
    return state.call.sidebar.peersTab ?? PeersTabValue.Conference;
};

export const selectPeersTab = (state: RootState) => {
    return state.call.sidebar.peersTab;
};

export const selectIsWaitingRoomTab = (state: RootState) => {
    return (
        getSidebarContent(state) === SidebarContent.Participants &&
        selectSidebarPeersTab(state) === PeersTabValue.WaitingRoom
    );
};

export const getChatId = (state: RootState) => {
    // Чат могли создать мы сами, либо уже кто-то создал
    // Если создал чат кто-то другой - в стейте придет поле от бека
    // Если мы - то берем из стора
    // Если в конфе создана трансляция, то у чата трансляции приоритет выше
    const conferenceSettings = state.call.conference.settings;
    return (
        conferenceSettings?.broadcast_data?.broadcast_chat_path ||
        conferenceSettings?.chat_path ||
        state.call.chat.chatId
    );
};

export const isParticipantModerator = (state: RootState, id: ParticipantID): boolean => {
    return Boolean(state.call.conference.participantsByIds[id]?.isModerator);
};

export const isParticipantOwner = (state: RootState, id: ParticipantID): boolean => {
    return Boolean(state.call.conference.participantsByIds[id]?.isOwner);
};

export const isSelfModerator = (state: RootState): boolean => {
    return isParticipantModerator(state, SELF);
};

export const isSelfOwner = (state: RootState): boolean => {
    return isParticipantOwner(state, SELF);
};

export const getBroadcastData = (state: RootState) => state.call.conference.settings?.broadcast_data;

export const getVhliveUgcStatus = (state: RootState) => state.call.broadcast?.vhlive?.ugc_live_status;

export const getBroadcastStatus = (state: RootState) => {
    const status = getBroadcastData(state)?.status || BroadcastStatus.NONE;
    const ugcLiveStatus = getVhliveUgcStatus(state);

    if (status === BroadcastStatus.STARTED) {
        switch (ugcLiveStatus) {
            case 'onair':
                return BroadcastStatus.STARTED;
            case 'finished':
                return BroadcastStatus.FINISHED;
            default:
                return BroadcastStatus.STARTING;
        }
    }

    return status;
};

export const canControlBroadcast = (state: RootState) => Boolean(state.user) && isSelfModerator(state);

/**
 * Соорганизатор конференции с созданной трансляцией должен иметь возможность ей управлять
 * даже если у него нет флага enabled
 *
 * @param {RootState} state
 * @returns {boolean}
 */
const isBroadcastEnabledToModerator = (state: RootState) =>
    canControlBroadcast(state) && getBroadcastStatus(state) !== BroadcastStatus.NONE && !isMessengerIntegration(state);

/**
 * Флаг allowed означает что пользователю надо показывать контролы создания трансляций (сейчас это b2b пользователи)
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const isBroadcastAllowed = (state: RootState) =>
    (Boolean(state.user?.broadcastAllowed) || isBroadcastEnabledToModerator(state)) && !isMessengerIntegration(state);

/**
 * Флаг enabled означает что пользователь может управлять трансляцией (b2b с тарифом "оптимальный" или "продвинутый")
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const selectIsBroadcastEnabled = (state: RootState) =>
    (isBroadcastAllowed(state) && Boolean(state.user?.broadcastFeatureEnabled)) || isBroadcastEnabledToModerator(state);

export const isConferenceWithBroadcast = (state: RootState) => getBroadcastStatus(state) !== BroadcastStatus.NONE;

export const getBroadcastUri = (state: RootState) => getBroadcastData(state)?.broadcast_uri;

export const getBroadcastStartedAt = (state: RootState) =>
    state.call.broadcast.vhlive.ugc_live_status === 'onair' ? state.call.broadcast.vhlive.start_time : undefined;

export const getBroadcastViewersCount = (state: RootState) => state.call.broadcast.vhlive.viewers;

export const getBroadcastTutorialStep = (state: RootState) => state.config.broadcastTutorialStep;

export const selectYaServices = (state: RootState) => state.config.yaServices;

export const getBroadcastStarterUid = (state: RootState) => getBroadcastData(state)?.started_by;

export const getBroadcastStarterParticipant = (state: RootState) => {
    const starterUid = getBroadcastStarterUid(state);
    const participant = Object.values(state.call.conference.participantsByIds).find(
        (participant) => participant?.uid === starterUid
    );
    return participant && getParticipant(state, participant.id);
};

export const isOtherUserBroadcasting = (state: RootState) => {
    const starterUid = getBroadcastStarterUid(state);
    const broadcastStatus = getBroadcastStatus(state);
    const { user } = state;
    return (!user || user.uid !== starterUid) && broadcastStatus === BroadcastStatus.STARTED;
};

export const isCurrentUserBroadcasting = (state: RootState) => {
    const starterUid = getBroadcastStarterUid(state);
    const broadcastStatus = getBroadcastStatus(state);
    const { user } = state;
    return user?.uid === starterUid && broadcastStatus === BroadcastStatus.STARTED;
};

const groupRegex = /\d{1,4}/g;
export const getFormattedCallNumber = createSelector(getConferenceId, (callNumber: string) => {
    return callNumber.match(groupRegex)?.join(' ') ?? '';
});

const selectOtherAssignedParticipantsCount = ({
    call: {
        conference: { slots },
    },
}: RootState) => slots.current.filter((slot) => Boolean(slot.participant)).length;

export const selectIsOneParticipant = createSelector(
    [selectOtherAssignedParticipantsCount, (state: RootState) => state],
    (otherAssignedParticipantsCount, { call }: RootState) => {
        const {
            conference: { conferenceStatus },
            videoGrid: { currentPage },
        } = call;

        return (
            conferenceStatus === ConferenceStatus.JOINED && currentPage === 0 && otherAssignedParticipantsCount === 0
        );
    }
);

const selectStreamUri = (state: RootState) => getBroadcastData(state)?.stream_uri;

export const getUgcLiveUrl = createSelector([selectStreamUri], (streamUri?: string) => {
    if (!streamUri) {
        return;
    }

    let streamUrl = streamUri;
    const streamUrlObject = new URL(streamUri);
    // Добавляем client_id (telemost_front) в начало pathname, если ручка от CloudAPI
    if (streamUrlObject.pathname.startsWith('/v2/telemost/')) {
        const origin = streamUrlObject.origin;
        const restUrl = streamUrlObject.pathname + streamUrlObject.search + streamUrlObject.hash;
        streamUrl = `${origin}/${CLIENT_ID}${restUrl}`;
    }

    return `${streamUrl}.json`;
});

export const isNonProductionEnvironment = (state: RootState) => state.config.currentHost !== 'telemost.yandex.ru';

export const getConferenceAccessLevel = (state: RootState): AccessLevel =>
    state.call.conference.settings?.access_level ?? AccessLevel.PUBLIC;

export const getBroadcastAccessLevel = (state: RootState): AccessLevel =>
    state.call.conference.settings?.broadcast_data?.access_level ?? AccessLevel.PUBLIC;

export const isAccessRestrictionOrganizationAllowedSelector = (state: RootState): boolean =>
    Boolean(state.call.conference.settings?.access_restriction_organization_allowed) &&
    !selectIsMeetingRoomIntegration(state);

export const canCreateOrganizationRestrictedConferenceSelector = (state: RootState): boolean =>
    Boolean(state.user?.canCreateOrganizationRestrictedConference);

export const isConferenceAccessLevelPublicSelector = (state: RootState): boolean =>
    getConferenceAccessLevel(state) === AccessLevel.PUBLIC;

export const isShareConferenceLinkSuccessSelector = (state: RootState): boolean =>
    Boolean(state.call.shareLink.shareConferenceLinkSuccess);

export const getConferenceShareLink = createSelector(
    [getConferenceConfig, isProductionEnvironmentSelector, getConfig],
    (conferenceConfig, isProduction, config) => {
        const conferenceUri = conferenceConfig?.uri;
        const { ruTelemostConferenceHost } = config;

        if (!conferenceUri) {
            return;
        }

        const url = new URL(conferenceUri);

        if (!isProduction || Boolean(ruTelemostConferenceHost)) {
            url.hostname = window.location.hostname;

            return url.toString();
        }

        return url.toString();
    }
);

export const getSelfMutedState = (state: RootState): { audioMuted: boolean; videoMuted: boolean } | null => {
    const participant = getParticipantById(state, SELF);

    if (!participant) {
        return null;
    }

    const audioMuted = participant.audio?.muted;
    const videoMuted = participant.video?.muted;

    return { audioMuted: Boolean(audioMuted), videoMuted: Boolean(videoMuted) };
};

export const selectIsPermissionsExtendedEnabled = (state: RootState) =>
    Boolean(state.call.conference.config?.client_configuration?.extended_role_permissions_enabled);

export const selectIsWaitingRoomEnabled = (state: RootState): boolean =>
    Boolean(state.call.conference.config?.waiting_room_available);

export const selectCanAdmitAll = (state: RootState) =>
    selectIsPermissionsExtendedEnabled(state)
        ? selectHasAdmitAllPermission(state)
        : Boolean(
              state.call.permissions && state.call.permissions.personalAllowed.includes(PersonalPermission.ADMIT_ALL)
          );

export const selectCanAdmit = (state: RootState) =>
    selectIsPermissionsExtendedEnabled(state)
        ? selectHasAdmitPermission(state)
        : Boolean(state.call.permissions && state.call.permissions.personalAllowed.includes(PersonalPermission.ADMIT));

export const selectCanWatchWaitingRoom = (state: RootState) =>
    selectIsWaitingRoomEnabled(state) &&
    (selectCanAdmitAll(state) || selectCanAdmit(state)) &&
    !selectIsMeetingRoomIntegration(state);

export const selectSelfMutedStateByKind = (state: RootState, kind: DeviceKind) => {
    const muteState = getSelfMutedState(state);

    if (!muteState) {
        return true;
    }
    const { audioMuted, videoMuted } = muteState;

    if (kind === 'audioinput') {
        return audioMuted;
    }
    if (kind === 'videoinput') {
        return videoMuted;
    }
};

export const selectWaitingRoomPeersSort = (state: RootState) => state.call.waitingRooms.waitingRoomPeersSort;
export const selectWaitingRoomPeers = (state: RootState) => state.call.waitingRooms.waitingRoomPeers;

export const selectSortedWaitingRoomPeers = (state: RootState) => {
    const sort = selectWaitingRoomPeersSort(state);
    return [...state.call.waitingRooms.waitingRoomPeers].sort((peer1, peer2) => {
        if (sort === WaitingRoomPeersSort.Alfa) {
            return peer1?.displayName?.localeCompare(peer2?.displayName || '', LANG, { numeric: true }) || -1;
        } else {
            return new Date(peer1.waitingRoomEnteredAt) < new Date(peer2.waitingRoomEnteredAt) ? -1 : 1;
        }
    });
};

export const selectWaitingRoomPeersCount = (state: RootState) => state.call.waitingRooms.waitingRoomPeers.length;

export const selectWaitingRoomIsEmpty = (state: RootState) => selectWaitingRoomPeersCount(state) === 0;

export const selectHasNewWaitingRoomPeers = (state: RootState) => {
    return !selectWaitingRoomIsEmpty(state) && state.call.waitingRooms.hasNewWaitingRoomPeers;
};

export const selectShouldShowNewWaitingRoomPeersIndicator = (state: RootState) => {
    return !selectIsWaitingRoomTab(state) && selectHasNewWaitingRoomPeers(state);
};

export const selectConferenceLimit = (state: RootState) => state.call.conference.config?.conference_limit;

export const selectIsWaitingRoomOverflow = (state: RootState) => {
    const conferenceLimit = selectConferenceLimit(state);
    const participantsCount = getParticipantsCount(state);
    const waitingRoomPeersCount = selectWaitingRoomPeersCount(state);

    return conferenceLimit && conferenceLimit - participantsCount < waitingRoomPeersCount;
};

export const selectConferenceIsFull = (state: RootState) => {
    const conferenceLimit = selectConferenceLimit(state);
    const participantsCount = getParticipantsCount(state);

    return conferenceLimit && conferenceLimit === participantsCount;
};

export const selectConferenceIsOverflow = (state: RootState) =>
    selectConferenceIsFull(state) && !selectWaitingRoomIsEmpty(state);

export const selectIsPresenterView = (state: RootState) => {
    const activeGridView = getActiveGridView(state);
    return activeGridView === GRID_VIEW.PRESENTER;
};

export const selectIsReactionsAvailable = (state: RootState) =>
    Boolean(state.call.conference.config?.client_configuration?.reactions_available);

export const selectSelfReactionType = (state: RootState) =>
    state.call.conference.participantsByIds[selectSyncStateWithPeerId(state) || SELF]?.reaction?.reactionType;

export const selectCanMuteAllPeerReactions = (state: RootState) =>
    Boolean(
        state.call.permissions && state.call.permissions.personalAllowed.includes(PersonalPermission.REACTIOM_MUTE_ALL)
    );

export const selectCanMutePeerReaction = (state: RootState) =>
    Boolean(
        state.call.permissions && state.call.permissions.personalAllowed.includes(PersonalPermission.REACTION_MUTE)
    );

export const selectHasNonSelfParticipantRaisedHand = createSelector([getParticipants], (participants) => {
    return participants.some((participant) => participant.id !== SELF && participant.reaction?.reactionType === 'hand');
});

export const selectHasNonSelfParticipantSuppressibleRaisedHand = createSelector([getParticipants], (participants) => {
    return participants.some(
        (participant) =>
            participant.id !== SELF &&
            participant.reaction?.reactionType === 'hand' &&
            participant.reaction?.suppressible
    );
});

export const selectHasNonSelfParticipantRecordingActive = createSelector([getParticipants], (participants) =>
    participants.some((participant) => participant.id !== SELF && participant.isLocalRecording)
);

/**
 * Любая запись активна (локальная своя, локальная других, облачная)
 */
export const selectIsAnyRecordingActive = createSelector(
    isLocalRecordingActive,
    selectHasNonSelfParticipantRecordingActive,
    selectIsCloudRecordingRunning,
    (myRecording, othersRecording, cloudRecording) => myRecording || othersRecording || cloudRecording
);

export const selectSlotsReceived = (state: RootState) => state.call.conference.slotsReceived;

export const selectShouldRenderInviteBlock = (state: RootState) => {
    return (
        !selectIsTeleMessenger(state) &&
        !isInsideIframe() &&
        !isMobileUa(state) &&
        selectIsOneParticipant(state) &&
        getDesktopSharingStatus(state) !== 'local' &&
        selectSlotsReceived(state)
    );
};

export const selectVisibleParticipantsList = createSelector([getParticipants], (participants) => {
    return participants.filter((participant) => !participant.refParticipantId);
});

export const selectVisibleParticipantsListCount = createSelector([selectVisibleParticipantsList], (participants) => {
    return participants.length;
});

const selectShouldForceDefaultSlotsGridView = createSelector(
    [getIsWaitingForCallee, getCallee, selectShouldRenderInviteBlock, getConferenceStatus],
    (isWaitingForCallee, callee, shouldRenderInviteBlock, conferenceStatus) =>
        !(isWaitingForCallee && Boolean(callee)) &&
        (shouldRenderInviteBlock || conferenceStatus !== ConferenceStatus.JOINED)
);

/** Вид сетки слотов с учётом принудительного DEFAULT (приглашение / до join), как в GoloomVideoGrid. */
export const selectEffectiveSlotsGridView = createSelector(
    [selectShouldForceDefaultSlotsGridView, getActiveGridView],
    (shouldForceDefault, activeGridView) => (shouldForceDefault ? GRID_VIEW.DEFAULT : activeGridView)
);

export const selectMaxSlotsCount = createSelector(
    [selectEffectiveSlotsGridView, selectMaxDisplayParticipants],
    (effectiveGridView, maxDisplayParticipants) =>
        effectiveGridView === GRID_VIEW.PRESENTER ? maxDisplayParticipants + 1 : maxDisplayParticipants
);

export const selectSlotsGridPageOffset = createSelector(
    [selectMaxSlotsCount, getCurrentPage],
    (maxSlotsCount, currentPage) => maxSlotsCount * currentPage
);

export const selectShowPrevSlotsPageButton = createSelector(
    [selectSlotsGridPageOffset, selectIsFullscreen],
    (offset, isFullscreen) => !isFullscreen && offset > 0
);

export const selectShowNextSlotsPageButton = createSelector(
    [selectSlotsGridPageOffset, selectMaxDisplayParticipants, selectSlotsMaxOffset, selectIsFullscreen],
    (offset, maxDisplayParticipants, maxOffset, isFullscreen) =>
        !isFullscreen && offset + maxDisplayParticipants <= maxOffset
);

export const canChangePage = (state: RootState, direction: -1 | 1): boolean => {
    if (direction === -1) {
        return selectShowPrevSlotsPageButton(state);
    }
    return selectShowNextSlotsPageButton(state);
};

export const selectSortedParticipants = createSelector([selectVisibleParticipantsList], sortParticipants);

export const selectIsB2BConference = (state: RootState) => {
    return Boolean(state.call.conference.config?.is_legal_entity);
};

export const selectIsConferenceB2BDomainMismatch = (state: RootState) => {
    const isB2BHost = selectIsB2BHost(state);
    const isB2BConference = selectIsB2BConference(state);

    return isB2BHost !== isB2BConference;
};

export const selectRecordStartTime = (state: RootState) => state.call.record.recordStartTime;

export const selectOrganizationId = (state: RootState) => state.call.conference.config?.organization_id || null;

export const selectSessionId = (state: RootState) => state.call.conference.config?.session_id || null;

export const selectBadNetworkQualityNotificationWasShown = (state: RootState) =>
    state.call.networkQuality.badNetworkQualityNotificationWasShown;

export const selectStatsEventBaseData: (state: RootState) => Omit<StatsEventBase, 'event_type'> = (state) => {
    return {
        room_id: getRoomId(state),
        peer_id: getMyUserId(state) || null,
        organization_id: selectOrganizationId(state),
        session_id: selectSessionId(state),
        platform: isMessengerIntegration(state) || selectIsTeleMessenger(state) ? 'web-messenger' : 'web',
        version: getAppVersionSelector(state),
        is_user_b2b: isB2bUserSelector(state),
    };
};

export const getDisplayMediaStatus = (state: RootState) => state.call.screenSharing.displayMediaStatus;
export const selectIsSharingStarting = (state: RootState) => state.call.screenSharing.isSharingStarting;
export const selectIsNewGrid = createSelector(
    [selectToggleNewGrid, getConferenceConfig],
    (toggleNewGrid, conferenceConfig): boolean => {
        if (!toggleNewGrid) {
            return Boolean(conferenceConfig?.client_configuration?.new_grid);
        }
        return !conferenceConfig?.client_configuration?.new_grid;
    }
);

export const selectMeetingName = (state: RootState) => state.call.conference.settings?.meeting_name;

export const selectIsPermissionsExtendedActive = (state: RootState) =>
    selectIsPermissionsExtendedEnabled(state) && selectHasConferencePermissionsEditMatrixPermission(state);

export const selectIsCalendarSummarizationReceiveAvailable = (state: RootState) =>
    Boolean(state.call.conference.config?.client_configuration?.calendar_summarization_receive_available);

export const selectConferencePhones = (state: RootState) => state.call.conference.config?.phones;

export const selectIsConferencePhoneAvail = (state: RootState) =>
    Number(state.call.conference.config?.phones?.length) > 0;
